/**
 * Partify YMM Paint Filter
 * Guards #variant-selector against stale YMM paint-code narrowing.
 * Shopify product variants remain the canonical paint-code source on PDPs.
 */
(function () {
	"use strict";

	const ALWAYS_SHOW = [
		"unpainted",
		"custom paint code",
		"we'll contact you",
	];
	const PAINT_MATCH_SENTINELS = [
		".vin",
		"vin",
		"match paint by vin",
		"match by vin",
		"paint code match by vin",
		"match paint by license plate",
		"match by license plate",
		"match paint by license",
		"match by license",
		"paint code match by license plate",
	];
	const TWO_TONE_HIDDEN_DATA_KEY = "partifyTwoToneHidden";
	const TWO_TONE_CONTROL_DISABLED_DATA_KEY =
		"partifyTwoToneOriginalDisabled";
	const TWO_TONE_CONTAINER_HIDDEN_DATA_KEY =
		"partifyTwoToneOriginalHidden";
	const TWO_TONE_MANUAL_VARIANT_DATA_KEY =
		"partifyTwoToneManualVariant";
	const TWO_TONE_MANUAL_PAINT_DATA_KEY =
		"partifyTwoToneManualPaint";
	const TWO_TONE_PAINT_MATCH_CONTROL_SELECTOR = [
		"#checkbox-get-paint-code-with-vin",
		"#checkbox-get-paint-code-with-license",
		"#fake-checkbox-get-paint-code-with-license-or-vin",
		"#checkbox-paint-code-local-storage",
		".get-paint-code-using-vin",
		"#select-vin-variant",
	].join(", ");

	function getCurrentVehicleHandles() {
		try {
			const terms = JSON.parse(localStorage.getItem("searchTerms") || "[]");
			if (!Array.isArray(terms) || !terms.length || !terms[0]) return [];
			return window.PartifyTwoToneRuleResolver.vehicleHandles(terms[0]);
		} catch (e) {
			return [];
		}
	}

	function getPaintCodesForVehicle() {
		const map = window.__PARTIFY_YMM_PAINT_MAP;
		if (!map || !Object.keys(map).length) return null;
		const handles = getCurrentVehicleHandles();
		const matchedHandle = handles.find(function (handle) {
			return Array.isArray(map[handle]) && map[handle].length;
		});
		return matchedHandle ? map[matchedHandle] : null;
	}

	function getTwoToneRuleConfig() {
		const config = window.__PARTIFY_TWO_TONE_PAINT_RULES;
		return config && Array.isArray(config.rules) ? config.rules : [];
	}

	function isTwoToneSelected() {
		if (document.documentElement.dataset.partifyTwoTonePaint === "true") {
			return true;
		}
		if (typeof twoToneSelected === "boolean") return twoToneSelected;
		return false;
	}

	function isManualPaintModeSelected() {
		const manualRadio = document.getElementById("checkbox-select-paint-option");
		return !!(manualRadio && manualRadio.checked && !manualRadio.disabled);
	}

	function syncTwoTonePaintMatchingUi(refreshDisabledBaseline) {
		const active = isTwoToneSelected();
		const container = document.querySelector(".paint-options-vin-license");

		if (active) {
			document.documentElement.dataset.partifyTwoTonePaint = "true";
			if (container) {
				if (container.dataset[TWO_TONE_CONTAINER_HIDDEN_DATA_KEY] === undefined) {
					container.dataset[TWO_TONE_CONTAINER_HIDDEN_DATA_KEY] = container.hidden
						? "true"
						: "false";
				}
				container.hidden = true;
			}
		} else if (
			container &&
			container.dataset[TWO_TONE_CONTAINER_HIDDEN_DATA_KEY] !== undefined
		) {
			container.hidden =
				container.dataset[TWO_TONE_CONTAINER_HIDDEN_DATA_KEY] === "true";
			delete container.dataset[TWO_TONE_CONTAINER_HIDDEN_DATA_KEY];
		}

		document
			.querySelectorAll(TWO_TONE_PAINT_MATCH_CONTROL_SELECTOR)
			.forEach(function (control) {
				if (active) {
					if (
						refreshDisabledBaseline ||
						control.dataset[TWO_TONE_CONTROL_DISABLED_DATA_KEY] === undefined
					) {
						control.dataset[TWO_TONE_CONTROL_DISABLED_DATA_KEY] = control.disabled
							? "true"
							: "false";
					}
					if ("checked" in control) control.checked = false;
					control.disabled = true;
					return;
				}

				if (control.dataset[TWO_TONE_CONTROL_DISABLED_DATA_KEY] === undefined) {
					return;
				}
				control.disabled =
					control.dataset[TWO_TONE_CONTROL_DISABLED_DATA_KEY] === "true";
				delete control.dataset[TWO_TONE_CONTROL_DISABLED_DATA_KEY];
			});

		if (!active) {
			clearTwoToneManualDecision();
			return;
		}
		document
			.querySelectorAll(
				'#paintcode-app-container, #paintcode-app-container-license, #vin-decoder-after-selection, [id^="vin-after-paint-code-selection-"]',
			)
			.forEach(function (element) {
				element.classList.remove("show");
			});
	}

	function getTwoTonePaintCodesForVehicle() {
		const rules = getTwoToneRuleConfig();
		if (!rules.length) return null;
		if (!isTwoToneSelected() || !isManualPaintModeSelected()) return null;

		const handles = getCurrentVehicleHandles();
		if (!handles.length) return null;

		const selected = window.PartifyTwoToneRuleResolver.selectRule(rules, handles);
		return selected && selected.codes.length ? selected.codes : null;
	}

	function isAlwaysVisible(text) {
		const t = (text || "").toLowerCase();
		return ALWAYS_SHOW.some(function (p) {
			return t.indexOf(p) !== -1;
		});
	}

	function isPaintMatchSentinel(text) {
		const t = String(text || "").toLowerCase().trim();
		if (!t) return false;
		return PAINT_MATCH_SENTINELS.some(function (p) {
			return t === p || t.indexOf(p) !== -1;
		});
	}

	function showAllOptions(select) {
		Array.from(select.options).forEach(function (opt) {
			if (opt.dataset.partifyPaintMatchOriginalDisabled) {
				opt.disabled = opt.dataset.partifyPaintMatchOriginalDisabled === "true";
				delete opt.dataset.partifyPaintMatchOriginalDisabled;
			}
			opt.hidden = false;
			delete opt.dataset[TWO_TONE_HIDDEN_DATA_KEY];
		});
	}

	function getOptionPaintMatchText(opt) {
		return [
			opt.text || "",
			opt.label || "",
			opt.getAttribute("data-variant-title") || "",
			opt.getAttribute("data-variant-sku") || "",
		].join(" ");
	}

	function normalizePaintCodes(paintCodes) {
		const normalized = new Set();
		paintCodes.forEach(function (c) {
			const code = (c || "").toLowerCase().trim();
			if (!code) return;
			normalized.add(code);
			if (code.indexOf("wa") === 0 && code.length > 2) {
				normalized.add(code.slice(2));
			}
		});
		return normalized;
	}

	function optionMatchesCodes(opt, normalized) {
		const optText = (opt.text || "").toLowerCase().trim();
		return Array.from(normalized).some(function (code) {
			return (
				optText === code ||
				optText.startsWith(code + " ") ||
				optText.startsWith(code + "+")
			);
		});
	}

	function optionIsFilteredOut(opt) {
		return opt.hidden;
	}

	function wouldHideProductPaintCode(select, normalized) {
		return Array.from(select.options).some(function (opt) {
			if (!opt.value || opt.disabled || isAlwaysVisible(opt.text)) return false;
			return !optionMatchesCodes(opt, normalized);
		});
	}

	function applyYmmPaintFilter(select) {
		const paintCodes = getPaintCodesForVehicle();
		if (!paintCodes || !paintCodes.length) return;

		// Paint codes in __PARTIFY_YMM_PAINT_MAP can be handles with a GM "WA"
		// prefix (e.g. "wa102v") while dropdown labels show the bare code ("102V").
		const normalized = normalizePaintCodes(paintCodes);
		if (!normalized.size) return;

		// The YMM paint-code map is an inferred helper, not an authoritative product
		// exclusion list. If it would hide any Shopify variant option, keep the full
		// product list so partial or restored vehicle state cannot remove valid codes.
		if (wouldHideProductPaintCode(select, normalized)) return;

		Array.from(select.options).forEach(function (opt) {
			if (!opt.value || opt.disabled || isAlwaysVisible(opt.text)) {
				opt.hidden = false;
				return;
			}
			const match = optionMatchesCodes(opt, normalized);
			opt.hidden = !match;
		});
	}

	function applyTwoTonePaintFilter(select) {
		const paintCodes = getTwoTonePaintCodesForVehicle();
		if (!paintCodes || !paintCodes.length) return;

		const normalized = normalizePaintCodes(paintCodes);
		if (!normalized.size) return;
		Array.from(select.options).forEach(function (opt) {
			if (!opt.value || opt.disabled || isAlwaysVisible(opt.text)) {
				opt.hidden = false;
				delete opt.dataset[TWO_TONE_HIDDEN_DATA_KEY];
				return;
			}
			if (optionIsFilteredOut(opt)) return;
			const hidden = !optionMatchesCodes(opt, normalized);
			opt.hidden = hidden;
			if (hidden) opt.dataset[TWO_TONE_HIDDEN_DATA_KEY] = "true";
			else delete opt.dataset[TWO_TONE_HIDDEN_DATA_KEY];
		});
	}

	function clearProductFormVariantIds() {
		const forms = Array.from(
			document.querySelectorAll(
				'form[action*="/cart/add"], #cnm-form, #product-form',
			),
		);
		forms.forEach(function (form) {
			form.querySelectorAll('[name="id"]').forEach(function (input) {
				input.value = "";
			});
			if (form.action) form.action = "/cart/add?id=";
		});
	}

	function clearSubmittedPaintMatchSentinels() {
		document
			.querySelectorAll(
				'input[name="properties[Paint Code]"], input[name="properties[Touch-Up Color]"]',
			)
			.forEach(function (input) {
				if (isPaintMatchSentinel(input.value)) input.value = "";
			});
	}

	function clearSubmittedPaintProperties() {
		document
			.querySelectorAll(
				'input[name="properties[Paint Code]"], input[name="properties[Touch-Up Color]"]',
			)
			.forEach(function (input) {
				input.value = "";
			});
	}

	function clearTwoToneManualDecision() {
		const group = document.querySelector(".paint-options-checkbox-group");
		const select = document.getElementById("variant-selector");
		if (group) delete group.dataset[TWO_TONE_MANUAL_PAINT_DATA_KEY];
		if (select) delete select.dataset[TWO_TONE_MANUAL_VARIANT_DATA_KEY];
	}

	function selectedTwoToneVariantIsUsable(select) {
		if (!select || !select.value) return false;
		const option = select.options[select.selectedIndex];
		return !!(
			option &&
			!option.disabled &&
			!option.hidden &&
			option.getAttribute("data-unavailable") !== "true" &&
			option.getAttribute("data-available") !== "false"
		);
	}

	// Entering Two Colors is a new customer decision. Keep manual paint mode open,
	// but clear every prior/default paint value and its submitted variant id.
	function resetForManualTwoToneEntry() {
		const group = document.querySelector(".paint-options-checkbox-group");
		const manualRadio = document.getElementById("checkbox-select-paint-option");
		const select = document.getElementById("variant-selector");

		clearTwoToneManualDecision();
		if (group) {
			group
				.querySelectorAll('input[name="select_paint_option"]')
				.forEach(function (radio) {
					radio.checked = false;
				});
		}
		if (manualRadio) {
			manualRadio.checked = true;
			manualRadio.dispatchEvent(new Event("change", { bubbles: true }));
		}
		if (select) select.value = "";
		clearProductFormVariantIds();
		clearSubmittedPaintProperties();
		if (typeof disableAddToCartButton === "function") {
			disableAddToCartButton();
		}
	}

	function hasExplicitMainTwoTonePaint() {
		const group = document.querySelector(".paint-options-checkbox-group");
		const checked = group && group.querySelector(
			'input[name="select_paint_option"]:checked',
		);
		if (!group || !checked) return false;
		if (checked.id === "checkbox-select-unpainted") {
			return group.dataset[TWO_TONE_MANUAL_PAINT_DATA_KEY] === "unpainted";
		}
		if (checked.id !== "checkbox-select-paint-option") return false;

		const select = document.getElementById("variant-selector");
		return !!(
			selectedTwoToneVariantIsUsable(select) &&
			select.dataset[TWO_TONE_MANUAL_VARIANT_DATA_KEY] === select.value
		);
	}

	function excludeTwoTonePaintMatchOptions(select) {
		if (!isTwoToneSelected()) return;
		Array.from(select.options).forEach(function (opt) {
			if (!opt.value || !isPaintMatchSentinel(getOptionPaintMatchText(opt))) return;
			if (!opt.dataset.partifyPaintMatchOriginalDisabled) {
				opt.dataset.partifyPaintMatchOriginalDisabled = opt.disabled
					? "true"
					: "false";
			}
			opt.hidden = true;
			opt.dataset[TWO_TONE_HIDDEN_DATA_KEY] = "true";
			opt.disabled = true;
		});
	}

	function resetFilteredSelection(select) {
		const current = select.options[select.selectedIndex];
		if (current && optionIsFilteredOut(current)) {
			if (isTwoToneSelected()) {
				// Two-tone requires an explicit manual choice — never auto-fill Unpainted,
				// the prior one-color code, or "first visible option."
				select.value = "";
				clearTwoToneManualDecision();
				clearProductFormVariantIds();
				clearSubmittedPaintMatchSentinels();
				if (typeof disableAddToCartButton === "function") {
					disableAddToCartButton();
				}
				return;
			}
			const firstVisible = Array.from(select.options).find(function (o) {
				return !optionIsFilteredOut(o);
			});
			if (firstVisible) {
				select.value = firstVisible.value;
				clearProductFormVariantIds();
				clearSubmittedPaintMatchSentinels();
				if (typeof disableAddToCartButton === "function") {
					disableAddToCartButton();
				}
			}
		}
	}

	function filterVariantDropdown(refreshPaintMatchBaseline) {
		const select = document.getElementById("variant-selector");
		if (select && select.options.length) {
			showAllOptions(select);
			applyYmmPaintFilter(select);
			applyTwoTonePaintFilter(select);
			excludeTwoTonePaintMatchOptions(select);
			resetFilteredSelection(select);
		}

		syncTwoTonePaintMatchingUi(refreshPaintMatchBaseline === true);
	}

	window.PartifyTwoTonePaint = window.PartifyTwoTonePaint || {};
	window.PartifyTwoTonePaint.syncAfterVariantRender = function () {
		filterVariantDropdown(true);
	};
	window.PartifyTwoTonePaint.resetForManualEntry = resetForManualTwoToneEntry;
	window.PartifyTwoTonePaint.clearManualDecision = clearTwoToneManualDecision;
	window.PartifyTwoTonePaint.hasExplicitMainSelection =
		hasExplicitMainTwoTonePaint;

	document.addEventListener("variantsUpdated", function () {
		requestAnimationFrame(function () {
			requestAnimationFrame(filterVariantDropdown);
		});
	});

	document.addEventListener("DOMContentLoaded", function () {
		requestAnimationFrame(filterVariantDropdown);
	});

	document.addEventListener("change", function (event) {
		const target = event.target;
		if (target?.id === "variant-selector" && isTwoToneSelected()) {
			if (event.isTrusted !== true) {
				// Programmatic restores/defaults are never a fresh two-tone decision.
				// Reject them even when the caller did not tag the synthetic event.
				target.value = "";
				clearTwoToneManualDecision();
				clearProductFormVariantIds();
				clearSubmittedPaintProperties();
				if (typeof disableAddToCartButton === "function") {
					disableAddToCartButton();
				}
			} else if (selectedTwoToneVariantIsUsable(target)) {
				target.dataset[TWO_TONE_MANUAL_VARIANT_DATA_KEY] = target.value;
			} else {
				delete target.dataset[TWO_TONE_MANUAL_VARIANT_DATA_KEY];
			}
		}

		if (
			target?.matches?.('input[name="select_paint_option"]') &&
			isTwoToneSelected()
		) {
			const group = target.closest(".paint-options-checkbox-group");
			const select = document.getElementById("variant-selector");
			const trustedChoice = event.isTrusted === true;
			if (group) {
				if (
					trustedChoice &&
					target.checked &&
					target.id === "checkbox-select-unpainted"
				) {
					group.dataset[TWO_TONE_MANUAL_PAINT_DATA_KEY] = "unpainted";
				} else {
					delete group.dataset[TWO_TONE_MANUAL_PAINT_DATA_KEY];
				}
			}
			if (
				select &&
				(target.id !== "checkbox-select-paint-option" || !trustedChoice)
			) {
				delete select.dataset[TWO_TONE_MANUAL_VARIANT_DATA_KEY];
			}
			if (!trustedChoice) {
				// Defaults such as initPaintOptionDefaults() dispatch plain synthetic
				// changes. Keep manual mode open and visibly unanswered instead.
				const manualRadio = document.getElementById(
					"checkbox-select-paint-option",
				);
				if (target.id !== "checkbox-select-paint-option") target.checked = false;
				if (manualRadio) manualRadio.checked = true;
				if (select) select.value = "";
				clearProductFormVariantIds();
				clearSubmittedPaintProperties();
				if (typeof disableAddToCartButton === "function") {
					disableAddToCartButton();
				}
			}
		}

		if (
			target?.matches?.(".fitment-radio, input[name='select_paint_option']")
		) {
			requestAnimationFrame(filterVariantDropdown);
		}
	});

	window.addEventListener("searchTermsUpdated", function () {
		requestAnimationFrame(filterVariantDropdown);
	});

	window.addEventListener("partify:vehicle:changed", function () {
		requestAnimationFrame(filterVariantDropdown);
	});
})();
